import { Geolocation } from '@capacitor/geolocation'
import dayjs from 'dayjs'
import { defineStore } from 'pinia'
import useDate from 'src/CrudManager/Core/composables/useDate'
import { useUserStore } from 'src/CrudManager/Core/stores/useUserStore'
import { axiosApi } from 'src/CrudManager/Core/Utils/axios'
import { i18n } from 'src/CrudManager/Core/Utils/i18n'
import { showErrorNotification, showSuccessNotification } from 'src/CrudManager/Core/Utils/ToastNotification'
import type { ServiceCallIntervention } from 'src/CrudManager/CrudModels/service_call/types'
import { computed, ref } from 'vue'

const { formatDate } = useDate()

export interface Location {
  latitude: number | null
  longitude: number | null
}

export interface BaseRouteableData {
  id: string
  status: string
  created_by_id?: string
  created_at?: string
  updated_at?: string
}

export type RouteableData = ServiceCallIntervention

export interface Routeable<T extends BaseRouteableData = RouteableData> {
  id: string
  work_day_id: string
  routeable_id: string
  routeable_type: string
  punched_at: string | null
  left_at: string | null
  order: number
  created_at: string
  updated_at: string
  routeable_data: T
}

export interface PunchStatus {
  id: string
  date: string
  punched_at: string | null
  type: 'IN' | 'OUT' | 'BREAK_START' | 'BREAK_END'
  comment: string | null
  location: Location | null
  user_id: string
  work_day_id: string
  updated_at: string | null
}

export interface WorkDay {
  id: string
  user_id: string
  date: string
  type: string
  routeables: Routeable[]
  punches: PunchStatus[]
  created_at: string
  updated_at: string
  notes: string | null
  expected_hours: string | null
  location: Location | null
  attachment_path: string | null
  time_slots: string | null
  break_duration: number
  status: string
  created_by_id: string
  approved_by_id: string | null
  approved_at: string | null
}

export const usePunchStore = defineStore(
  'punch-store',
  () => {
    const workDays = ref<WorkDay[]>([])

    const workToday = computed(() => {
      if (workDays.value.length === 0) {
        return null
      }

      const today = dayjs().format('YYYY-MM-DD')
      const day = workDays.value.find((workDay) => formatDate(workDay.date) == formatDate(today))

      return day || null
    })

    const routeables = computed(() => workToday.value?.routeables || [])

    const updateRouteables = (newRouteables: Routeable[]) => {
      if (workToday.value) {
        workToday.value.routeables = [...newRouteables]
        // Force reactivity by creating a new reference
        workDays.value = [...workDays.value]
      }
    }

    const hasPunchedInToday = computed(() => {
      return punchStatuses.value.find((punch) => punch.type === 'IN' && punch.punched_at !== null)
    })

    const hasPunchedOutToday = computed(() => {
      return punchStatuses.value.find((punch) => punch.type === 'OUT' && punch.punched_at !== null)
    })

    const currentStep = computed(() => {
      return hasPunchedInToday.value ? 2 : 1
    })

    const punchStatuses = computed(() => {
      return workToday.value?.punches || []
    })

    const getRouteableById = (id: string) => {
      return routeables.value.find((routeable) => routeable.id === id)
    }

    const getRouteableByDate = (date: string) => {
      const workDay = workDays.value.find((workDay) => workDay.date === date)
      return workDay?.routeables || []
    }

    const canPunch = (punchStatus: PunchStatus) => {
      if (!punchStatus) {
        return true
      }

      // Check if the punch belongs to the current user
      if (punchStatus.user_id !== userStore.user?.id) {
        return false
      }

      // If trying to punch out, check if there's a punched-in record for the same date
      if (punchStatus.type === 'OUT') {
        const inPunch = punchStatuses.value.find((punch) => punch.type === 'IN' && punch.punched_at !== null)
        if (!inPunch) {
          return false
        }
      }

      // If trying to start a break, must be punched in and not already on break
      if (punchStatus.type === 'BREAK_START') {
        const inPunch = punchStatuses.value.find((punch) => punch.type === 'IN' && punch.punched_at !== null)
        if (!inPunch) {
          return false
        }

        // Check if there's already an ongoing break
        const breakStartPunches = punchStatuses.value.filter((punch) => punch.type === 'BREAK_START' && punch.punched_at !== null)

        const breakEndPunches = punchStatuses.value.filter((punch) => punch.type === 'BREAK_END' && punch.punched_at !== null)

        // If no break starts, can start a break
        if (breakStartPunches.length === 0) {
          return true
        }

        // If equal number of start/end, can start a new break
        if (breakStartPunches.length === breakEndPunches.length) {
          return true
        }

        // Otherwise, must be more starts than ends, meaning a break is in progress
        return false
      }

      // If trying to end a break, must be on break
      if (punchStatus.type === 'BREAK_END') {
        const breakStartPunches = punchStatuses.value.filter((punch) => punch.type === 'BREAK_START' && punch.punched_at !== null)

        const breakEndPunches = punchStatuses.value.filter((punch) => punch.type === 'BREAK_END' && punch.punched_at !== null)

        // If no break starts, can't end a break
        if (breakStartPunches.length === 0) {
          return false
        }

        // If equal number of start/end, can't end a break (no active break)
        if (breakStartPunches.length === breakEndPunches.length) {
          return false
        }

        // Otherwise, must be more starts than ends, meaning a break is in progress
        return true
      }

      return true
    }

    const userStore = useUserStore()

    const getWorkDay = async (date: string | null = null) => {
      date = date || dayjs().format('YYYY-MM-DD')

      const { data } = await axiosApi.get(`punch/${date}`)

      if (!data.workDay) {
        return null
      }

      const index = workDays.value.findIndex((day) => formatDate(day.date) === formatDate(date))
      if (index !== -1) {
        // Create new references for nested objects
        const updatedWorkDay = {
          ...data.workDay,
          routeables: [...data.workDay.routeables],
          punches: [...data.workDay.punches],
        }
        workDays.value[index] = updatedWorkDay
      } else {
        workDays.value.push(data.workDay)
      }

      // Force reactivity
      workDays.value = [...workDays.value]
    }

    const punch = async (type: 'IN' | 'OUT' | 'BREAK_START' | 'BREAK_END', comment: string) => {
      try {
        let punchStatus = getPunchStatusByType(type)

        if (!canPunch(punchStatus)) {
          throw new Error(i18n.t('composites.PunchComponent.errors.cannotPunchType', { type: type }))
        }

        const position = await Geolocation.getCurrentPosition().catch(() => {
          const errorMsg = i18n.t('composites.PunchComponent.errors.locationUnavailable')
          showErrorNotification(errorMsg, { position: 'bottom' })
          console.error(errorMsg)
        })

        punchStatus.punched_at = dayjs().format('YYYY-MM-DD HH:mm:ss')
        punchStatus.work_day_id = punchStatus.work_day_id || crypto.randomUUID()
        punchStatus.user_id = userStore.user?.id

        punchStatus.location = position && position.coords ? { latitude: position.coords.latitude, longitude: position.coords.longitude } : null
        punchStatus.comment = comment

        const response = await axiosApi.post(`punch/workday/punch`, {
          ...punchStatus,
          type: type,
        })

        if (response.data.punch) {
          punchStatus = response.data.punch
        }

        showSuccessNotification(i18n.t(`composites.PunchComponent.success.punch_${type.toLowerCase()}`), { position: 'bottom' })
      } catch (error) {
        showErrorNotification(
          error instanceof Error ? error.message : i18n.t('common.errors.unexpected'),
          { position: 'bottom', log: error },
        )
        throw error
      }
    }

    let syncInFlight: Promise<unknown> | null = null
    const syncPunchStatuses = async () => {
      if (syncInFlight) return syncInFlight
      const run = async () => {
      try {
        const response = await axiosApi.post('punch/sync', { workdays: workDays.value })
        if (response.data.workDay) {
          const newWorkDay = response.data.workDay

          const index = workDays.value.findIndex((day) => formatDate(day.date) === formatDate(newWorkDay.date))

          // Create new references for all nested objects
          const processedWorkDay = {
            ...newWorkDay,
            routeables: newWorkDay.routeables.map((routeable) => ({
              ...routeable,
              // Ensure all nested properties have new references
              location: routeable.location ? { ...routeable.location } : undefined,
              employees: routeable.employees ? routeable.employees.map((emp) => ({ ...emp })) : undefined,
            })),
            punches: newWorkDay.punches.map((punch) => ({
              ...punch,
              location: punch.location ? { ...punch.location } : null,
            })),
          }

          if (index !== -1) {
            workDays.value[index] = processedWorkDay
          } else {
            workDays.value.push(processedWorkDay)
          }

          // Force reactivity by creating a new array reference
          workDays.value = [...workDays.value]
        }

        return response.data
      } catch (error) {
        console.error('Error syncing work day:', error)
        throw error
      }
      }
      syncInFlight = run().finally(() => {
        syncInFlight = null
      })
      return syncInFlight
    }

    const getPunchStatusById = (id: string): PunchStatus | undefined => {
      const punch = punchStatuses.value.find((punch) => punch.id === id)

      if (!punch) {
        showErrorNotification(i18n.t('composites.PunchComponent.errors.punchNotFound', { id }), { position: 'bottom' })
      }

      return punch
    }

    const getPunchStatusByType = (type: 'IN' | 'OUT' | 'BREAK_START' | 'BREAK_END'): PunchStatus | undefined => {
      if (!workToday.value) {
        const newWorkDay: WorkDay = {
          type: type,
          date: dayjs().format('YYYY-MM-DD'),
          punches: [],
          routeables: [],
          created_at: dayjs().format('YYYY-MM-DD HH:mm:ss'),
          updated_at: null,
          notes: null,
          user_id: userStore.user?.id,
          id: crypto.randomUUID(),
          approved_at: null,
          expected_hours: null,
          location: null,
          attachment_path: null,
          time_slots: null,
          break_duration: 0,
          status: 'approved',
          created_by_id: userStore.user?.id,
          approved_by_id: null,
        }

        workDays.value.push(newWorkDay)
      }

      const result = punchStatuses.value.find((punch) => punch.type === type)

      // if no result, we need to create a new punch status and add it to the punchStatuses array
      if (!result) {
        const today = dayjs().format('YYYY-MM-DD')
        const newPunch: PunchStatus = {
          type: type,
          date: today,
          punched_at: null,
          work_day_id: workToday.value.id,
          user_id: userStore.user?.id,
          id: crypto.randomUUID(),
          comment: null,
          location: null,
          updated_at: null,
        }

        if (workToday.value.punches) {
          workToday.value.punches.push(newPunch)
        }
        return newPunch
      }

      return result
    }

    const isOnBreak = computed(() => {
      const breakStartPunches = punchStatuses.value.filter((punch) => punch.type === 'BREAK_START' && punch.punched_at !== null)

      const breakEndPunches = punchStatuses.value.filter((punch) => punch.type === 'BREAK_END' && punch.punched_at !== null)

      // If there are no break punches, user is not on break
      if (breakStartPunches.length === 0) return false

      // If there are no end break punches, user is on break
      if (breakEndPunches.length === 0) return true

      // If equal number, not on break
      if (breakStartPunches.length === breakEndPunches.length) return false

      // If more starts than ends, on break
      return breakStartPunches.length > breakEndPunches.length
    })

    return {
      currentStep,
      workDays,
      getWorkDay,
      workToday,
      punch,
      syncPunchStatuses,
      routeables,
      updateRouteables,
      getRouteableByDate,
      getRouteableById,
      getPunchStatusById,
      canPunch,
      hasPunchedInToday,
      hasPunchedOutToday,
      isOnBreak,
      getPunchStatusByType,
    }
  },
  {
    persist: false,
  },
)
